import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import productoService from '../../../services/productoService';
import productoDepositoService from '../../../services/productoDepositoService';
import kitService from '../../../services/kitService';
import { esTipoKit } from '../../../utils/stockUmVenta';
import { loadInfo360Tab, saveInfo360Tab } from '../../../utils/info360TabStorage';
import Info360Recommendation from '../../clientes/info360/components/Info360Recommendation';
import type { Info360Alerta, Info360Recomendacion } from '../../clientes/info360/types';
import ArticuloInfo360KpiRow from './components/ArticuloInfo360KpiRow';
import ArticuloInfo360TabResumen from './components/ArticuloInfo360TabResumen';
import ArticuloInfo360TabStock from './components/ArticuloInfo360TabStock';
import ArticuloInfo360TabVentas from './components/ArticuloInfo360TabVentas';
import ArticuloInfo360TabActividad from './components/ArticuloInfo360TabActividad';
import type { ProductoInfo360Maestro } from './components/ArticuloInfo360Header';
import type {
  ArticuloInfo360ActividadItem,
  ArticuloInfo360Documento,
  ArticuloInfo360Indicadores,
  ArticuloInfo360StockDeposito,
} from './types';
import { resolverAccionDocumentoArticulo } from './documentoAcciones';
import { resolveUnidadMedida } from './formatCantidad';
import {
  getCachedInfo360,
  isCacheFresh,
  setCachedIndicadores,
  setCachedMaestro,
} from './info360ProductoCache';

const PRODUCTO_TABS = ['resumen', 'stock', 'ventas', 'actividad'] as const;
type TabKey = (typeof PRODUCTO_TABS)[number];

function umDesdeProducto(producto: ProductoInfo360Maestro | Record<string, unknown>): string {
  const p = producto as ProductoInfo360Maestro;
  const nested = p.unidadMedida;
  const nestedId = nested && typeof nested === 'object'
    ? (nested.idUnidadMedida ?? nested.descripcion)
    : typeof nested === 'string' ? nested : null;
  return resolveUnidadMedida(p.idUnidadMedida, p.um, nestedId);
}

function mergeIndicadoresUm(
  maestro: ProductoInfo360Maestro,
  info360Data: ArticuloInfo360Indicadores,
): ArticuloInfo360Indicadores {
  const um = resolveUnidadMedida(info360Data.unidadMedida, umDesdeProducto(maestro));
  const stockBase: ArticuloInfo360StockDeposito[] = Array.isArray(info360Data.stockPorDeposito)
    ? info360Data.stockPorDeposito
    : [];
  return { ...info360Data, unidadMedida: um, stockPorDeposito: stockBase };
}

const KpiSkeleton: React.FC = () => (
  <Grid container spacing={1.5}>
    {[0, 1, 2, 3].map((i) => (
      <Grid item xs={12} sm={6} md={3} key={i}>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Skeleton width="60%" height={16} />
          <Skeleton width="40%" height={32} sx={{ mt: 1 }} />
          <Skeleton width="50%" height={14} sx={{ mt: 0.5 }} />
        </Paper>
      </Grid>
    ))}
  </Grid>
);

interface Props {
  codigoArticulo: string;
  renderHeader?: (producto: ProductoInfo360Maestro) => React.ReactNode;
  abrirDocumentosEnNuevaPestana?: boolean;
  accionSecundariaError?: { label: string; onClick: () => void };
}

const ArticuloInfo360Contenido: React.FC<Props> = ({
  codigoArticulo,
  renderHeader,
  abrirDocumentosEnNuevaPestana = false,
  accionSecundariaError,
}) => {
  const navigate = useNavigate();
  const { user, tenantId } = useAuth();

  const [producto, setProducto] = useState<ProductoInfo360Maestro | null>(null);
  const [indicadores, setIndicadores] = useState<ArticuloInfo360Indicadores | null>(null);
  const [loadingMaestro, setLoadingMaestro] = useState(true);
  const [loadingIndicadores, setLoadingIndicadores] = useState(true);
  const [loadingUbicaciones, setLoadingUbicaciones] = useState(false);
  const [loadingKitStock, setLoadingKitStock] = useState(false);
  const [errorMaestro, setErrorMaestro] = useState<string | null>(null);
  const [errorIndicadores, setErrorIndicadores] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>(() =>
    loadInfo360Tab('producto', user?.username, tenantId, PRODUCTO_TABS, 'resumen'),
  );

  const ubicacionesCargadasRef = useRef(false);
  const kitStockCargadoRef = useRef(false);
  const codigoRef = useRef('');

  const cargarMaestro = useCallback(async (codigo: string, options?: { useCache?: boolean; silent?: boolean }) => {
    const useCache = options?.useCache ?? true;
    const silent = options?.silent ?? false;

    if (useCache) {
      const cached = getCachedInfo360(codigo);
      if (cached?.maestro && isCacheFresh(cached, 'maestro')) {
        setProducto(cached.maestro);
        setLoadingMaestro(false);
        return cached.maestro;
      }
    }

    if (!silent) setLoadingMaestro(true);
    setErrorMaestro(null);
    try {
      const productoData = await productoService.getProducto(codigo, { soloOperativo: false });
      const maestro = productoData as ProductoInfo360Maestro;
      setCachedMaestro(codigo, maestro);
      setProducto(maestro);
      setIndicadores((prev) => (prev ? mergeIndicadoresUm(maestro, prev) : prev));
      return maestro;
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setErrorMaestro(status === 404
        ? `No se encontró el artículo "${codigo}".`
        : 'No se pudo cargar el maestro del artículo.');
      setProducto(null);
      return null;
    } finally {
      setLoadingMaestro(false);
    }
  }, []);

  const cargarIndicadores = useCallback(async (
    codigo: string,
    maestro: ProductoInfo360Maestro | null,
    options?: { useCache?: boolean; silent?: boolean },
  ) => {
    const useCache = options?.useCache ?? true;
    const silent = options?.silent ?? false;

    if (useCache) {
      const cached = getCachedInfo360(codigo);
      if (cached?.indicadores && isCacheFresh(cached, 'indicadores')) {
        const merged = maestro
          ? mergeIndicadoresUm(maestro, cached.indicadores)
          : cached.indicadores;
        setIndicadores(merged);
        setLoadingIndicadores(false);
        return merged;
      }
    }

    if (!silent) setLoadingIndicadores(true);
    setErrorIndicadores(null);
    try {
      const info360Data = await productoService.getProductoInfo360(codigo);
      const merged = maestro
        ? mergeIndicadoresUm(maestro, info360Data)
        : info360Data;
      setCachedIndicadores(codigo, merged);
      setIndicadores(merged);
      return merged;
    } catch {
      setErrorIndicadores('No se pudieron cargar los indicadores analíticos.');
      setIndicadores(null);
      return null;
    } finally {
      setLoadingIndicadores(false);
    }
  }, []);

  const cargarUbicaciones = useCallback(async (codigo: string) => {
    if (ubicacionesCargadasRef.current) return;
    setLoadingUbicaciones(true);
    try {
      const depositosRes = await productoDepositoService.getDepositos(codigo);
      ubicacionesCargadasRef.current = true;
      if (!depositosRes?.usaEstanterias) return;
      const depositosByNro = new Map(
        (Array.isArray(depositosRes.depositos) ? depositosRes.depositos : []).map((d) => [
          d.nroUbicacion,
          d,
        ]),
      );
      const labels = depositosRes.labels;
      setIndicadores((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          stockPorDeposito: (prev.stockPorDeposito ?? []).map((dep) => {
            const extra = depositosByNro.get(dep.nroUbicacion);
            return {
              ...dep,
              ubicacion1: extra?.ubicacion1 ?? null,
              ubicacion2: extra?.ubicacion2 ?? null,
              ubicacion3: extra?.ubicacion3 ?? null,
              ubicacion4: extra?.ubicacion4 ?? null,
              labels,
            };
          }),
        };
      });
    } catch (depositosErr) {
      console.error('Error al cargar ubicaciones del artículo en Info 360:', depositosErr);
    } finally {
      setLoadingUbicaciones(false);
    }
  }, []);

  const cargarKitStock = useCallback(async (codigo: string, maestro: ProductoInfo360Maestro) => {
    if (kitStockCargadoRef.current || !esTipoKit(maestro.tipoArticulo)) return;
    kitStockCargadoRef.current = true;
    setLoadingKitStock(true);
    try {
      const kitStock = await kitService.getStockKit(codigo);
      setIndicadores((prev) => {
        if (!prev) return prev;
        return { ...prev, kitStock };
      });
    } catch {
      /* sin composición */
    } finally {
      setLoadingKitStock(false);
    }
  }, []);

  const cargar = useCallback(async () => {
    const codigo = codigoArticulo.trim();
    if (!codigo) {
      setProducto(null);
      setIndicadores(null);
      setErrorMaestro('Código de artículo inválido.');
      setLoadingMaestro(false);
      setLoadingIndicadores(false);
      return;
    }

    codigoRef.current = codigo;
    ubicacionesCargadasRef.current = false;
    kitStockCargadoRef.current = false;

    const cached = getCachedInfo360(codigo);
    if (cached?.maestro) {
      setProducto(cached.maestro);
      setLoadingMaestro(false);
    }
    if (cached?.indicadores) {
      const merged = cached.maestro
        ? mergeIndicadoresUm(cached.maestro, cached.indicadores)
        : cached.indicadores;
      setIndicadores(merged);
      setLoadingIndicadores(false);
    }

    const hadCachedMaestro = Boolean(cached?.maestro);
    const hadCachedIndicadores = Boolean(cached?.indicadores);

    void cargarMaestro(codigo, { useCache: false, silent: hadCachedMaestro });
    void cargarIndicadores(codigo, cached?.maestro ?? null, { useCache: false, silent: hadCachedIndicadores });
  }, [codigoArticulo, cargarMaestro, cargarIndicadores]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  useEffect(() => {
    const codigo = codigoArticulo.trim();
    if (tab !== 'stock' || !codigo || !indicadores) return;

    void cargarUbicaciones(codigo);

    if (producto && esTipoKit(producto.tipoArticulo) && !indicadores.kitStock) {
      void cargarKitStock(codigo, producto);
    }
  }, [tab, codigoArticulo, producto, indicadores, cargarUbicaciones, cargarKitStock]);

  const abrirDocumento = useCallback((
    documento: ArticuloInfo360Documento | ArticuloInfo360ActividadItem,
  ) => {
    const accion = resolverAccionDocumentoArticulo(documento);
    if (!accion) return;

    if (abrirDocumentosEnNuevaPestana) {
      window.open(accion.path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(accion.path);
  }, [abrirDocumentosEnNuevaPestana, navigate]);

  const handleAbrirDocumento = useCallback((documento: ArticuloInfo360Documento) => {
    abrirDocumento(documento);
  }, [abrirDocumento]);

  const handleAbrirActividad = useCallback((actividad: ArticuloInfo360ActividadItem) => {
    abrirDocumento(actividad);
  }, [abrirDocumento]);

  if (loadingMaestro && !producto) {
    return (
      <Stack spacing={2.5}>
        <Skeleton variant="rounded" height={120} />
        <KpiSkeleton />
        <Skeleton variant="rounded" height={240} />
      </Stack>
    );
  }

  if (errorMaestro && !producto) {
    return (
      <Stack spacing={2} sx={{ py: 2 }}>
        <Alert severity="error">{errorMaestro}</Alert>
        <Stack direction="row" spacing={1}>
          {accionSecundariaError ? (
            <Button
              variant="outlined"
              onClick={accionSecundariaError.onClick}
              sx={{ textTransform: 'none' }}
            >
              {accionSecundariaError.label}
            </Button>
          ) : null}
          <Button variant="contained" onClick={() => void cargar()} sx={{ textTransform: 'none' }}>
            Reintentar
          </Button>
        </Stack>
      </Stack>
    );
  }

  if (!producto) {
    return (
      <Stack spacing={2} sx={{ py: 2 }}>
        <Alert severity="error">No hay datos para mostrar.</Alert>
      </Stack>
    );
  }

  const recomendacion = indicadores ? {
    codigo: indicadores.recomendacion.codigo,
    titulo: indicadores.recomendacion.titulo,
    detalle: indicadores.recomendacion.detalle,
    severidad: indicadores.recomendacion.severidad,
  } as unknown as Info360Recomendacion : null;
  const alertas = (indicadores?.alertas ?? []) as Info360Alerta[];

  return (
    <Stack spacing={2.5}>
      {renderHeader?.(producto)}

      {loadingIndicadores && !indicadores ? (
        <KpiSkeleton />
      ) : indicadores ? (
        <>
          <ArticuloInfo360KpiRow data={indicadores} />
          <Info360Recommendation recomendacion={recomendacion!} alertas={alertas} />
        </>
      ) : errorIndicadores ? (
        <Alert severity="warning" action={
          <Button color="inherit" size="small" onClick={() => void cargarIndicadores(codigoArticulo.trim(), producto, { useCache: false })}>
            Reintentar
          </Button>
        }>
          {errorIndicadores}
        </Alert>
      ) : null}

      <Box>
        <Tabs
          value={tab}
          onChange={(_, value: TabKey) => {
            setTab(value);
            saveInfo360Tab('producto', user?.username, tenantId, value);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Secciones Info 360 artículo"
        >
          <Tab label="Resumen" value="resumen" />
          <Tab label="Stock" value="stock" disabled={!indicadores && loadingIndicadores} />
          <Tab label="Ventas" value="ventas" disabled={!indicadores && loadingIndicadores} />
          <Tab label="Actividad" value="actividad" disabled={!indicadores && loadingIndicadores} />
        </Tabs>
      </Box>

      <Box role="tabpanel">
        {tab === 'resumen' ? (
          <ArticuloInfo360TabResumen
            producto={producto}
            data={indicadores}
            loadingIndicadores={loadingIndicadores}
          />
        ) : null}
        {tab === 'stock' && indicadores ? (
          <ArticuloInfo360TabStock
            data={indicadores}
            onAbrirDocumento={handleAbrirDocumento}
            loadingUbicaciones={loadingUbicaciones}
            loadingKitStock={loadingKitStock}
          />
        ) : null}
        {tab === 'ventas' && indicadores ? (
          <ArticuloInfo360TabVentas data={indicadores} onAbrirDocumento={handleAbrirDocumento} />
        ) : null}
        {tab === 'actividad' && indicadores ? (
          <ArticuloInfo360TabActividad
            data={indicadores}
            onAbrirDocumento={handleAbrirDocumento}
            onAbrirActividad={handleAbrirActividad}
          />
        ) : null}
      </Box>
    </Stack>
  );
};

export default ArticuloInfo360Contenido;
